import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useReducedMotion } from "framer-motion"
import { toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen, faPlus, faTrashAlt, faXmark } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import SchemaDrawing, { PieceGlyph } from "../lesson/schema/SchemaDrawing"
import SchemaEditor from "../lesson/schema/SchemaEditor"
import SchemaFullscreen from "../lesson/schema/SchemaFullscreen"
import { useBackClose } from "../atoms/useBackClose"
import { schemaPlayers } from "../utils/exerciseSchema.utils"
import { FULL_SCREEN, between, flipFrom, hasSize, insetOf, play } from "../lesson/schema/schemaMotion"
import { editNotebookEntry } from "../utils/firestore.utils"
import { networkAwareMessage } from "../utils/network.utils"
import {
	NOTEBOOK_EXERCISE,
	entryAt,
	exercisePhaseLabel,
	isMine,
	usesLabel,
	variantOf,
	withVariant,
} from "../utils/notebook.utils"
import { EmptyCourt, GlassChip } from "./NotebookGallery"
import {
	CopyIcon,
	NotebookShare,
	VariantNote,
	VariantTabs,
	useCopyToNotebook,
} from "./NotebookVariants"

// Un esercizio del quaderno, aperto: lo schema protagonista. Il campo sta
// in alto a tutta larghezza, sul suo blu che arriva fino ai bordi, senza la
// cornice verde; sopra, la fase su un vetro e la ✕. Sotto sale il foglio
// col nome, quanti giocatori, la fase, gli usi e il testo.
//
// A cavallo dei due c'è un tasto solo, "Modifica schema": apre l'editor,
// che ha lo stesso blu, e il campo sembra crescere fino a riempire lo
// schermo. Lo schema si salva appena si tocca "Fatto". Togliere lo schema
// si fa da lì dentro: qui sotto un cestino non avrebbe detto se toglie lo
// schema o la voce. In fondo al foglio, "Modifica" (nome, fase e testo) e
// "Togli dal quaderno", che vale per tutta la voce e chiede conferma in un
// foglio (`removeNotebookEntry`).
//
// Senza schema, al posto del disegno c'è il campo vuoto sfocato col +.
//
// Sotto il nome ci sono le varianti, per livello, e la riga che dice cosa
// cambia: campo, testo e schema sono della variante scelta. Poi "Condividi
// con i colleghi". Di un collega non si cambia niente: niente Modifica né
// schema da aggiungere, ma di chi è e "Copia nel mio quaderno".
//
// Sta sopra al quaderno ma sotto i modal, perché "Modifica" ne apre uno.
//
// Si apre dalla carta toccata (`from` dice dov'è, e dov'è la sua
// copertina): la pagina si allarga dal riquadro della carta fino allo
// schermo, il blu della copertina diventa il blu in alto e lo schema va al
// suo posto; il foglio sale per ultimo. La ✕ fa il contrario e torna nella
// carta. Il blu che si allarga è un velo a parte, fermo sullo schermo, che
// a fine corsa lascia il posto al fondo vero del campo, quello che scorre
// con la pagina.
//
// Si chiude anche col tasto indietro del telefono (o lo swipe dal bordo) e
// trascinandola giù, come un foglio, quando è in cima: lasciata dopo un bel
// tratto, o tirata in fretta, scende via; se no torna su. Toccando lo schema
// lo si guarda a schermo pieno: lì sopra ci sono la stessa fase e la stessa
// ✕, allo stesso posto, così in alto non cambia niente mentre si apre e si
// chiude; "Modifica schema" intanto si toglie di mezzo, come mentre
// l'editor è aperto.

const c = theme.colors

const pill = (bg, color) => ({
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	gap: "0.5rem",
	height: "2.75rem",
	padding: "0 1.125rem",
	border: 0,
	borderRadius: "22px",
	background: bg,
	color,
	font: "inherit",
	fontWeight: 700,
	cursor: "pointer",
})

// Aprendo, la fase, la ✕ e il + arrivano a schema fermo, e il foglio sale
// subito dopo di lui; chiudendo se ne vanno per primi
const OPENING = {
	chips: { delay: 0.2, duration: 0.25 },
	add: { delay: 0.2, duration: 0.25 },
	sheet: { delay: 0.08, duration: 0.4 },
}
const CLOSING = { chips: { duration: 0.12 }, add: { duration: 0.12 }, sheet: { duration: 0.2 } }

const Tag = ({ children }) => (
	<span
		style={{
			display: "inline-flex",
			alignItems: "center",
			gap: "0.375rem",
			height: "1.75rem",
			padding: "0 0.75rem",
			borderRadius: "14px",
			background: c.gunmetal_lighter,
			color: c.gunmetal,
			fontSize: "0.75rem",
			fontWeight: 600,
		}}>
		{children}
	</span>
)

// Le sagome dei giocatori dello schema, in fila, nei loro colori
const PlayersIcon = ({ players }) => (
	<svg
		viewBox={`0 0 ${12 + players.length * 7} 20`}
		height="16"
		width={(12 + players.length * 7) * 0.8}
		aria-hidden="true">
		{players.map((player, i) => (
			<g key={i} transform={`translate(${8 + i * 7},17) scale(0.62)`}>
				<PieceGlyph item={{ ...player, num: 0 }} />
			</g>
		))}
	</svg>
)

function NotebookExerciseView({ entry: stored, variantID: openedWith, from, onClose }) {
	const Modal = useContext(modalContext)
	const { copy, isCopying } = useCopyToNotebook()
	const [variantID, setVariantID] = useState(openedWith ?? stored.variantID)
	// la variante che si guardava se n'è andata: si torna alla prima
	const entry = variantOf(stored, variantID) ? entryAt(stored, variantID) : stored
	const canEdit = isMine(entry)
	const [isDrawing, setDrawing] = useState(false)
	const [isFull, setFull] = useState(false)
	const reduceMotion = useReducedMotion()
	const parts = {
		root: useRef(null),
		blue: useRef(null),
		hero: useRef(null),
		court: useRef(null),
		chips: useRef(null),
		add: useRef(null),
		sheet: useRef(null),
	}
	const motion = useRef(null)
	const leaving = useRef(false)

	// Com'è la pagina aperta, e com'è da chiusa: ritagliata sulla carta, il
	// blu sulla copertina e lo schema sopra di lei. Una carta senza schema,
	// nella lista, non ha copertina: il blu e lo schema compaiono al loro
	// posto. Se la carta non c'è più (o non ci sono misure, come nei test)
	// niente animazione.
	const states = () => {
		const court = parts.court.current?.getBoundingClientRect()
		const hero = parts.hero.current?.getBoundingClientRect()
		const card = from?.()
		if (reduceMotion || !hasSize(court) || !hasSize(hero) || !hasSize(card?.card)) return null
		const cover = hasSize(card.cover)
		return {
			open: {
				root: { clipPath: FULL_SCREEN },
				blue: { clipPath: insetOf(hero), opacity: 1 },
				court: { x: 0, y: 0, scale: 1, opacity: 1 },
				chips: { opacity: 1 },
				add: { opacity: 1 },
				sheet: { y: 0, opacity: 1 },
			},
			closed: {
				root: { clipPath: insetOf(card.card, 15) },
				blue: cover
					? { clipPath: insetOf(card.cover, card.coverRadius), opacity: 1 }
					: { clipPath: insetOf(hero), opacity: 0 },
				court: cover ? { ...flipFrom(card.cover, court), opacity: 1 } : { x: 0, y: 0, scale: 0.94, opacity: 0 },
				chips: { opacity: 0 },
				add: { opacity: 0 },
				sheet: { y: 48, opacity: 0 },
			},
		}
	}

	// Mentre si muove, il blu è il velo; ferma, il fondo vero. Il fondo si
	// riscrive per intero: vuoto, il telefono lo toglierebbe e basta, e il
	// campo resterebbe su un fondo chiaro
	const showVeil = on => {
		parts.hero.current.style.background = on ? "transparent" : c.schema_court
		parts.blue.current.style.visibility = on ? "visible" : "hidden"
	}

	const run = (from, to, enter) => {
		const els = Object.fromEntries(Object.entries(parts).map(([name, ref]) => [name, ref.current]))
		return play(between(els, from, to, { enter, timing: enter ? OPENING : CLOSING }), {
			duration: enter ? 0.42 : 0.34,
		})
	}

	// all'apertura, e solo allora
	useLayoutEffect(() => {
		const both = states()
		if (!both) return
		showVeil(true)
		motion.current = run(both.closed, both.open, true)
		motion.current.finished.then(() => !leaving.current && parts.hero.current && showVeil(false))
		return () => motion.current?.stop()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const close = () => {
		if (leaving.current) return
		parts.root.current.scrollTop = 0
		const both = states()
		if (!both) return onClose()
		leaving.current = true
		parts.root.current.style.pointerEvents = "none"
		motion.current?.stop()
		Object.assign(parts.blue.current.style, both.open.blue)
		showVeil(true)
		run(both.open, both.closed, false).finished.then(onClose)
	}

	useBackClose(close)

	// Trascinata giù quando è in cima: la pagina segue il dito e sotto si
	// vede il quaderno. Col dito si ascolta il tocco e non il puntatore: solo
	// così, mentre la si tira, la pagina non prova anche a scorrere.
	const closeRef = useRef(close)
	closeRef.current = close
	useEffect(() => {
		const root = parts.root.current
		let start = null
		let pull = 0
		// mentre scende ha gli angoli tondi in alto, come un foglio
		const place = y => {
			root.style.transform = y > 0 ? `translateY(${y}px)` : ""
			root.style.borderRadius = y > 0 ? "26px 26px 0 0" : ""
		}
		const onStart = event => {
			root.style.transition = ""
			start =
				leaving.current || event.touches.length !== 1 || root.scrollTop > 0
					? null
					: { x: event.touches[0].clientX, y: event.touches[0].clientY, at: performance.now(), pulling: false }
		}
		const onMove = event => {
			if (!start) return
			const dx = event.touches[0].clientX - start.x
			const dy = event.touches[0].clientY - start.y
			if (!start.pulling) {
				if (Math.abs(dy) < 10) return
				// in su, di lato, o a pagina già scorsa: è uno scorrimento
				if (dy < 0 || Math.abs(dx) > Math.abs(dy) || root.scrollTop > 0) return (start = null)
				start.pulling = true
			}
			event.preventDefault()
			pull = Math.max(0, dy)
			place(pull)
		}
		const onEnd = () => {
			if (!start?.pulling) return (start = null)
			const speed = pull / Math.max(1, performance.now() - start.at)
			start = null
			// il resto della corsa lo fa il browser, con una transizione: arrivata
			// in fondo la pagina resta lì finché si chiude. Con un'animazione
			// fatta a mano, finita, il telefono la rimetteva per un attimo dov'era
			// il dito, e il campo ricompariva in un lampo prima di sparire.
			if (pull > 120 || speed > 0.6) {
				leaving.current = true
				root.style.pointerEvents = "none"
				root.style.transition = "transform 0.26s ease-in"
				root.style.transform = `translateY(${window.innerHeight}px)`
				// si toglie a corsa finita, quando è già tutta fuori dallo schermo
				setTimeout(onClose, 300)
			} else {
				root.style.transition = "transform 0.22s ease-out, border-radius 0.22s ease-out"
				place(0)
				setTimeout(() => {
					if (!start) root.style.transition = ""
				}, 240)
			}
			pull = 0
		}
		root.addEventListener("touchstart", onStart, { passive: true })
		root.addEventListener("touchmove", onMove, { passive: false })
		root.addEventListener("touchend", onEnd)
		root.addEventListener("touchcancel", onEnd)
		return () => {
			root.removeEventListener("touchstart", onStart)
			root.removeEventListener("touchmove", onMove)
			root.removeEventListener("touchend", onEnd)
			root.removeEventListener("touchcancel", onEnd)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Il fondo del campo che si vede davvero: il riquadro in alto fin dove
	// comincia il foglio, che gli sale sopra con gli angoli tondi. Chi si
	// allarga da qui (l'editor, lo schema a schermo pieno) e ci torna si ferma
	// lì: fermandosi più giù, sopra al bordo tondo del foglio, quel pezzo
	// restava coperto fino all'ultimo e poi saltava fuori di colpo.
	const heroArea = () => {
		const hero = parts.hero.current?.getBoundingClientRect()
		const sheetTop = parts.sheet.current?.getBoundingClientRect().top
		if (!hasSize(hero) || sheetTop === undefined) return hero
		return { left: hero.left, top: hero.top, width: hero.width, height: Math.max(0, sheetTop - hero.top) }
	}

	// L'editor cresce dal campo in alto: lo schema da lì, il blu dal suo
	// riquadro
	const editorOrigin = () => ({
		court: parts.court.current?.getBoundingClientRect(),
		area: heroArea(),
	})

	const players = schemaPlayers(entry.schema)

	const failed = (error, fallback) => {
		const message = networkAwareMessage(error, fallback)
		toast.error(message, { toastId: message, position: "top-center" })
	}

	const saveSchema = async schema => {
		setDrawing(false)
		try {
			await editNotebookEntry(entry.entryID, {
				kind: NOTEBOOK_EXERCISE,
				title: entry.title,
				phase: entry.phase,
				variants: withVariant(stored, entry.variantID, { schema }),
			})
		} catch (error) {
			failed(error, "Lo schema non è stato salvato")
		}
	}

	return createPortal(
		<div
			ref={parts.root}
			role="dialog"
			aria-modal="true"
			aria-label={entry.title}
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 900,
				overflowY: "auto",
				background: c.background,
				color: c.text,
				fontFamily: theme.fontFamily.primary,
				// il foglio riempie quello che il campo lascia: i tasti in fondo
				// stanno nello schermo, senza dover scorrere
				display: "flex",
				flexDirection: "column",
			}}>
			{/* il blu che si allarga dalla copertina, mentre si apre e si chiude */}
			<div
				ref={parts.blue}
				aria-hidden="true"
				style={{
					position: "fixed",
					inset: 0,
					background: c.schema_court,
					visibility: "hidden",
					pointerEvents: "none",
				}}
			/>
			{/* il campo, a tutta larghezza */}
			<div
				ref={parts.hero}
				style={{
					position: "relative",
					flex: "none",
					background: c.schema_court,
					paddingTop: "calc(env(safe-area-inset-top) + 1.5rem)",
					paddingBottom: "1.5rem",
				}}>
				<div
					ref={parts.court}
					style={{ position: "relative", width: "min(100%, 30rem)", margin: "0 auto", aspectRatio: "358 / 400" }}>
					{entry.schema ? (
						// toccato, lo schema si guarda a schermo pieno
						<button
							type="button"
							aria-label={`Apri lo schema: ${entry.title}`}
							onClick={() => {
								// lo schema a schermo pieno rifà la fase e la ✕ al loro posto
								// sullo schermo: la voce dev'essere in cima, dove stanno loro
								parts.root.current.scrollTop = 0
								setFull(true)
							}}
							style={{
								display: "block",
								width: "100%",
								height: "100%",
								padding: 0,
								border: 0,
								background: "none",
								cursor: "zoom-in",
							}}>
							<SchemaDrawing schema={entry.schema} grass={false} label={`Schema: ${entry.title}`} />
						</button>
					) : (
						<>
							<EmptyCourt grass={false} />
							{canEdit && (
								<button
									ref={parts.add}
									type="button"
									onClick={() => setDrawing(true)}
									style={{
										position: "absolute",
										inset: 0,
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										justifyContent: "center",
										gap: "0.625rem",
										border: 0,
										background: "transparent",
										font: "inherit",
										cursor: "pointer",
									}}>
									<span
										aria-hidden="true"
										style={{
											width: "4rem",
											height: "4rem",
											borderRadius: "50%",
											background: c.primary,
											color: c.white,
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											boxShadow: theme.shadows.lift,
										}}>
										<FontAwesomeIcon icon={faPlus} size="xl" />
									</span>
									<GlassChip style={{ height: "2.125rem", fontSize: "0.8125rem" }}>Aggiungi lo schema</GlassChip>
								</button>
							)}
						</>
					)}
				</div>
				<div
					ref={parts.chips}
					style={{
						position: "absolute",
						left: "1rem",
						right: "1rem",
						top: "calc(env(safe-area-inset-top) + 1rem)",
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}>
					<GlassChip style={{ height: "2.125rem", fontSize: "0.8125rem" }}>
						{exercisePhaseLabel(entry.phase)}
					</GlassChip>
					<button
						type="button"
						aria-label="Chiudi"
						onClick={close}
						style={{
							width: "2.25rem",
							height: "2.25rem",
							borderRadius: "50%",
							border: 0,
							background: c.background_blur,
							backdropFilter: "blur(8px)",
							WebkitBackdropFilter: "blur(8px)",
							color: c.gunmetal,
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
						}}>
						<FontAwesomeIcon icon={faXmark} />
					</button>
				</div>
			</div>

			{/* il foglio, che sale sopra il fondo del campo */}
			<div
				ref={parts.sheet}
				style={{
					position: "relative",
					marginTop: "-1.75rem",
					flex: 1,
					padding: "2.25rem 1.25rem calc(env(safe-area-inset-bottom) + 1.5rem)",
					borderRadius: "26px 26px 0 0",
					background: c.background,
					display: "flex",
					flexDirection: "column",
					gap: "0.75rem",
				}}>
				{entry.schema && canEdit && (
					<button
						type="button"
						onClick={() => setDrawing(true)}
						style={{
							...pill(c.primary, c.white),
							position: "absolute",
							right: "1.25rem",
							top: "-1.375rem",
							// il bagliore dei bottoni arancioni: un'ombra cupa si allungava
							// sul titolo, e nello scuro lo sbiadiva
							boxShadow: theme.shadows.accent,
							// con lo schema a schermo pieno o nell'editor si toglie di mezzo:
							// tornando al suo posto il campo ci passerebbe sopra, e dall'editor
							// riapparirebbe a metà, poi l'altra metà a scatto. Torna quando
							// lo schema è fermo, con una transizione del browser e basta
							opacity: isFull || isDrawing ? 0 : 1,
							transition: "opacity 0.18s ease-out",
						}}>
						<FontAwesomeIcon icon={faPen} />
						Modifica schema
					</button>
				)}
				<div style={{ fontSize: "1.375rem", fontWeight: 700, lineHeight: 1.2, color: c.gunmetal }}>
					{entry.title}
				</div>
				<div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
					{players.length > 0 && (
						<Tag>
							<PlayersIcon players={players} />
							{players.length === 1 ? "1 giocatore" : `${players.length} giocatori`}
						</Tag>
					)}
					<Tag>{exercisePhaseLabel(entry.phase)}</Tag>
					<Tag>{usesLabel(entry)}</Tag>
				</div>
				<VariantTabs
					entry={entry}
					selected={entry.variantID}
					onSelect={setVariantID}
					onAdd={
						canEdit
							? () =>
									Modal.open("notebookEntry", {
										entryID: entry.entryID,
										variantID: entry.variantID,
										asNewVariant: true,
									})
							: null
					}
					ground={c.surface}
					bleed="1.25rem"
				/>
				<VariantNote entry={entry} variant={entry} />
				{entry.text && (
					<div
						style={{
							background: c.surface,
							borderRadius: "15px",
							padding: "0.75rem 1rem",
							lineHeight: 1.45,
							whiteSpace: "pre-line",
						}}>
						{entry.text}
					</div>
				)}
				<NotebookShare entry={entry} ground={c.surface} />
				<div
					style={{
						marginTop: "auto",
						paddingTop: "1rem",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: "0.75rem",
					}}>
					{canEdit ? (
						<>
							<button
								type="button"
								onClick={() =>
									Modal.open("notebookEntry", {
										entryID: entry.entryID,
										variantID: entry.variantID,
									})
								}
								style={{ ...pill(c.gunmetal_fill, c.white), alignSelf: "stretch", height: "3rem" }}>
								<FontAwesomeIcon icon={faPen} />
								Modifica
							</button>
							{/* chiede conferma in un foglio, con "No, macchè!" pieno */}
							<button
								type="button"
								onClick={() => Modal.open("removeNotebookEntry", { entryID: entry.entryID })}
								style={pill(c.red_light, c.red)}>
								<FontAwesomeIcon icon={faTrashAlt} />
								Togli dal quaderno
							</button>
						</>
					) : (
						<button
							type="button"
							disabled={isCopying}
							onClick={() => copy(entry)}
							style={{
								...pill("transparent", c.gunmetal),
								alignSelf: "stretch",
								height: "3rem",
								boxShadow: `inset 0 0 0 2px ${c.gunmetal}`,
							}}>
							<CopyIcon />
							{isCopying ? "Copio..." : "Copia nel mio quaderno"}
						</button>
					)}
				</div>
			</div>

			{isFull && entry.schema && (
				<SchemaFullscreen
					schema={entry.schema}
					label={entry.title}
					chip={exercisePhaseLabel(entry.phase)}
					origin={() => ({
						court: parts.court.current?.getBoundingClientRect(),
						area: heroArea(),
					})}
					onClose={() => setFull(false)}
				/>
			)}

			{isDrawing && (
				<SchemaEditor
					schema={entry.schema}
					title={entry.title}
					origin={editorOrigin}
					onCancel={() => setDrawing(false)}
					onDone={saveSchema}
				/>
			)}
		</div>,
		document.body,
	)
}

export default NotebookExerciseView
